import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Calendar, CalendarOptions } from '@fullcalendar/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { GlobalService } from 'src/app/api/global.service';

import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import { ModalController } from '@ionic/angular';
import { SetupComponent } from './setup/setup.component';
import { TimeSlotTypes } from './time-slot/time-slot.component';
import { CalendarEventTypes, CreateComponent } from './appointments/create/create.component';
import { DetailsPage as TaskDetailsPage } from 'src/app/tasks/details/details.page';
import { DetailsPage as EmailDetailsPage } from 'src/app/secure-email/details/details.page';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';
import { TimeAndBillingService } from 'src/app/time-and-billing/time-and-billing.service';

import * as dayjs from 'dayjs';

import { ProjectsService } from 'src/app/api/projects.service';
import { ApiService } from 'src/app/api/api.service';

@Component({
  selector: 'app-team-member-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent implements OnInit, OnDestroy {

  calendarOptions: CalendarOptions;

  @ViewChild('teammembercalendar', { static: false })
  calendarComponent: FullCalendarComponent;

  calendarAPI: Calendar = null;

  LoadedCalendar = false;
  ShowCalendar = false;
  loading_events = false;
  loading_team_members = false;

  @Input() team_member: any;
  @Input() contentHeight = 100;
  @Input() locked = true;

  team_members: any[] = [];
  selected_team_member_guid = '';
  combined_view = false;
  readonly ALL_TEAM_MEMBERS_VALUE = '__all__';

  multi_view_mode = false;
  selected_team_member_guids: string[] = [];

  show_calendar_setup = false;
  loading_setup = false;
  private setup_event_ids: string[] = [];

  // Show Only Time Tracking (the event filter below) draws the time entries in place of the calendar's events
  get time_tracking_mode(): boolean {
    return !this.locked && this.event_filter === 'time_tracking';
  }

  // What the calendar shows: appointments only by default (most users don't want their tasks on the calendar). The choice is
  // remembered in the browser.
  event_filter: 'all' | 'tasks' | 'appointments' | 'time_tracking' = (() => {
    try {
      const saved = localStorage.getItem('calendarEventFilter');
      if (saved === 'all' || saved === 'tasks' || saved === 'appointments' || saved === 'time_tracking') return saved;
    } catch { /* no storage - the default */ }
    return 'appointments';
  })();

  SetEventFilter(filter: 'all' | 'tasks' | 'appointments' | 'time_tracking') {
    if (this.event_filter === filter) return;
    this.event_filter = filter;
    try { localStorage.setItem('calendarEventFilter', filter); } catch { /* not remembered */ }
    this.RefreshCurrentView();
  }

  // Tasks are the assigned work on the calendar; blocked time stays with the appointments
  private ShowsEventType(type: any): boolean {
    if (this.locked || this.event_filter === 'all') return true;
    const is_task = type == CalendarEventTypes.assigned_work;
    return this.event_filter === 'tasks' ? is_task : !is_task;
  }

  get ShowingCombinedView(): boolean {
    return this.combined_view || (this.multi_view_mode && this.selected_team_member_guids.length > 0);
  }

  constructor(
    public globalService: GlobalService,
    private modalController: ModalController,
    private projectsService: ProjectsService,
    private apiService: ApiService,
    private timeAndBillingService: TimeAndBillingService
  ) {}

  time_entries: any[] = [];

  private time_changes_subscription: Subscription;

  ngOnDestroy() {
    this.time_changes_subscription?.unsubscribe();
  }

  ngOnInit() {
    // A time entry was created or deleted somewhere (the timer, the time tracking page...) - redraw the calendar
    this.time_changes_subscription = this.timeAndBillingService.changes$.subscribe(() => this.RefreshCurrentView());
    if (this.locked && this.team_member) {
      this.DrawCalendar();
    } else if (!this.locked) {
      this.LoadTeamMembers();
    }
  }

  async LoadTeamMembers() {
    this.loading_team_members = true;
    const data: any = await this.apiService.get('team_members');
    if (data?.success && data.data) {
      this.team_members = data.data;
      const my_guid = this.globalService.GetCurrentUser()?.guid;
      const match = this.team_members.find(tm => tm.guid === my_guid);
      this.selected_team_member_guid = match ? match.guid : (this.team_members[0]?.guid || '');
      await this.LoadSelectedTeamMember();
    }
    this.loading_team_members = false;
  }

  async LoadSelectedTeamMember() {
    this.combined_view = this.selected_team_member_guid === this.ALL_TEAM_MEMBERS_VALUE;

    if (this.combined_view) {
      if (!this.LoadedCalendar) {
        this.DrawCalendar();
      } else {
        this.RefreshCurrentView();
      }
      return;
    }

    const data: any = await this.apiService.get(`team_members/${this.selected_team_member_guid}`);
    if (data?.success) {
      this.team_member = Array.isArray(data.data) ? data.data[0] : data.data;
      if (!this.team_member.skills)   this.team_member.skills = [];
      if (!this.team_member.projects) this.team_member.projects = [];
      if (!this.team_member.tasks)    this.team_member.tasks = [];
      console.log("Selected Team Member:", this.team_member);
      if (!this.LoadedCalendar) {
        this.DrawCalendar();
      } else {
        this.RefreshCurrentView();
      }
    }
  }

  async OnTeamMemberChange() {
    await this.LoadSelectedTeamMember();
  }

  async Refresh() {
    if (this.calendarAPI && (this.multi_view_mode || this.combined_view || this.team_member)) {
      this.RefreshCurrentView();
    }
  }

  RefreshCurrentView() {
    if (!this.calendarAPI) return;
    if (this.time_tracking_mode) {
      this.GetTimeTrackingEvents(this.calendarAPI.view.currentStart, this.calendarAPI.view.currentEnd);
      return;
    }
    let loads: Promise<any> | null = null;
    if (this.multi_view_mode) {
      if (this.selected_team_member_guids.length === 0) {
        this.calendarAPI.removeAllEvents();
      } else {
        const subset = this.team_members.filter(tm => this.selected_team_member_guids.includes(tm.guid));
        loads = this.GetCombinedEvents(this.calendarAPI.view.currentStart, this.calendarAPI.view.currentEnd, subset);
      }
    } else if (this.combined_view) {
      loads = this.GetCombinedEvents(this.calendarAPI.view.currentStart, this.calendarAPI.view.currentEnd, this.team_members);
    } else if (this.team_member) {
      loads = this.GetUserEvents(this.calendarAPI.view.currentStart, this.calendarAPI.view.currentEnd, this.team_member.guid);
    }
    // Show All also shows the time entries, added once the calendar's own events are in
    if (loads && !this.locked && this.event_filter === 'all' && this.globalService.TimeAndBillingEnabled) {
      const start = this.calendarAPI.view.currentStart, end = this.calendarAPI.view.currentEnd;
      loads.then(() => { if (this.event_filter === 'all') this.GetTimeTrackingEvents(start, end, true); });
    }
    this.RefreshCalendarSetupOverlay();
  }

  // Only meaningful for a single team member's own calendar, and only in a view with a time axis -
  // dayGridMonth has no hours to shade, and combined/multi views have no single "whose setup" to show.
  get CanShowCalendarSetup(): boolean {
    if (this.multi_view_mode || this.combined_view) return false;
    const viewType = this.calendarAPI?.view?.type;
    return viewType === 'timeGridWeek' || viewType === 'timeGridDay';
  }

  ToggleShowCalendarSetup(checked: boolean) {
    if (checked && !this.CanShowCalendarSetup) return;
    this.show_calendar_setup = checked;
    this.RefreshCalendarSetupOverlay();
  }

  private GetSetupOverlayTargetGuids(): string[] {
    if (this.multi_view_mode) return this.selected_team_member_guids;
    if (this.combined_view) return this.team_members.map(tm => tm.guid);
    return this.team_member ? [this.team_member.guid] : [];
  }

  async RefreshCalendarSetupOverlay() {
    if (!this.calendarAPI) return;

    if (this.show_calendar_setup && !this.CanShowCalendarSetup) {
      this.show_calendar_setup = false;
    }

    // Clear any previously added setup overlay events before redrawing (or when turned off).
    this.setup_event_ids.forEach(id => {
      const ev = this.calendarAPI.getEventById(id);
      if (ev) ev.remove();
    });
    this.setup_event_ids = [];

    if (!this.show_calendar_setup) return;

    const target_guids = this.GetSetupOverlayTargetGuids();
    if (target_guids.length === 0) return;

    const showNames = target_guids.length > 1;
    this.loading_setup = true;

    const view_start = this.calendarAPI.view.currentStart;
    const view_end = this.calendarAPI.view.currentEnd;

    const results = await Promise.all(target_guids.map(async guid => {
      try {
        const data: any = await this.apiService.get(`calendar/setup/${guid}`);
        return { guid, slots: (data?.success && Array.isArray(data.data)) ? data.data : [] };
      } catch {
        return { guid, slots: [] };
      }
    }));

    results.forEach(({ guid, slots }) => {
      const tm_name = this.team_members.find(t => t.guid === guid)?.display_name || this.team_member?.display_name || '';
      slots.forEach((slot: any, slot_idx: number) => {
        const occurrences = this.BuildSetupSlotOccurrences(slot, view_start, view_end);
        const typeLabel = this.GetSetupSlotTypeLabel(slot.type);
        occurrences.forEach((occ, occ_idx) => {
          const id = `setup_${guid}_${slot_idx}_${occ_idx}`;
          this.calendarAPI.addEvent({
            id,
            title: showNames ? `${tm_name}: ${typeLabel}` : typeLabel,
            start: occ.start,
            end: occ.end,
            display: 'background',
            color: occ.colour
          });
          this.setup_event_ids.push(id);
        });
      });
    });

    this.loading_setup = false;
  }

  // Matches the option labels in time-slot.component.html's Type select.
  private GetSetupSlotTypeLabel(type: number): string {
    if (type == TimeSlotTypes.appointment) return 'Allow Appointments';
    if (type == TimeSlotTypes.work) return 'Allow Work';
    if (type == TimeSlotTypes.work_or_appointment) return 'Allow Work or Appointments';
    return 'Available';
  }

  // Walks every date in the visible calendar range day by day; whenever a date's weekday matches
  // the slot's day_of_week, produces a concrete occurrence for that date at the slot's start/end time.
  private BuildSetupSlotOccurrences(slot: any, view_start: Date, view_end: Date): { start: string; end: string; colour: string }[] {
    let colour = '#00ff00';
    if (slot.type == TimeSlotTypes.appointment) colour = '#ff9100';
    else if (slot.type == TimeSlotTypes.work) colour = '#004cff';
    else if (slot.type == TimeSlotTypes.work_or_appointment) colour = '#00ff00';

    const occurrences: { start: string; end: string; colour: string }[] = [];
    const cursor = new Date(view_start);
    cursor.setHours(0, 0, 0, 0);

    while (cursor < view_end) {
      if (cursor.getDay() === slot.day_of_week) {
        const start = this.UtcHHMMToLocalDate(slot.start_time, cursor, slot.tz_str);
        const end = this.UtcHHMMToLocalDate(slot.end_time, cursor, slot.tz_str);
        occurrences.push({ start: start.toISOString(), end: end.toISOString(), colour });
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return occurrences;
  }

  // Mirrors the tz shifting SetupComponent.convertToFullCalendarEvent uses: start_time/end_time are
  // stored as UTC HHMM integers, shifted here into a local Date on the given calendar day.
  private UtcHHMMToLocalDate(hhmm: number, baseDate: Date, tz_str?: string): Date {
    const tzStr = tz_str || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const utcStr = baseDate.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzDateStr = baseDate.toLocaleString('en-US', { timeZone: tzStr });
    const offsetMinutes = (new Date(tzDateStr).getTime() - new Date(utcStr).getTime()) / 60000;

    const totalMinutes = (Math.floor(hhmm / 100) * 60 + (hhmm % 100)) + offsetMinutes;
    let h = Math.floor(totalMinutes / 60) % 24;
    const m = ((totalMinutes % 60) + 60) % 60;
    if (h < 0) h += 24;

    const d = new Date(baseDate);
    d.setHours(h, m, 0, 0);
    return d;
  }

  SetMultiViewMode(enabled: boolean) {
    if (this.multi_view_mode === enabled) return;
    this.multi_view_mode = enabled;

    if (enabled) {
      // Default to the current user's own calendar so multi view isn't empty as soon as it's turned on.
      const my_guid = this.globalService.GetCurrentUser()?.guid;
      const default_guid = this.team_members.find(tm => tm.guid === my_guid)?.guid || this.team_members[0]?.guid;
      this.selected_team_member_guids = default_guid ? [default_guid] : [];
    } else {
      this.selected_team_member_guids = [];
    }

    if (!this.LoadedCalendar) {
      this.DrawCalendar();
    } else {
      this.RefreshCurrentView();
    }
  }

  IsTeamMemberSelected(guid: string): boolean {
    return this.selected_team_member_guids.includes(guid);
  }

  ToggleMultiTeamMember(guid: string) {
    const idx = this.selected_team_member_guids.indexOf(guid);
    if (idx >= 0) {
      this.selected_team_member_guids.splice(idx, 1);
    } else {
      this.selected_team_member_guids.push(guid);
    }
    this.RefreshCurrentView();
  }

  async presentCalendarSetup() {
    const modal = await this.modalController.create({
      component: SetupComponent,
      cssClass: 'large-modal',
      componentProps: {
        team_member: this.team_member
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        console.log(data.data);
      }
    });

    return await modal.present();
  }

  async DrawCalendar() {
    console.log("Drawing Calendar");

    this.calendarOptions = {
      initialView: 'dayGridMonth',

      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
        height: 'auto',
  expandRows: true,

      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],

      dateClick: this.handleDateClick.bind(this),
      eventClick: this.handleEventClick.bind(this),

      datesSet: () => {
        this.RefreshCurrentView();
      }
    };

    this.ShowCalendar = true;

    let count = 0;

    const interval = setInterval(() => {
      console.log("Calling Interval");

      if (this.calendarComponent) {

        this.calendarAPI = this.calendarComponent.getApi();

        if (!this.calendarAPI) {
          count++;
          if (count > 10) clearInterval(interval);
          return;
        }

        clearInterval(interval);

        console.log("Calendar Ready");
        this.RefreshCurrentView();

        this.LoadedCalendar = true;
        setTimeout(() => {
          this.calendarAPI.updateSize();
        }, 0);


      } else {

        console.log("Calendar Not Ready");

        count++;

        if (count > 10) clearInterval(interval);
      }

    }, 500);

  }

  async GetUserEvents(start_time, end_time, guid) {

    if (!this.calendarAPI) return;

    this.loading_events = true;
    this.calendarAPI.removeAllEvents();

    let event_data: any = await this.apiService.get(
      'calendar/events?team_member=' +
      guid +
      '&start_time=' +
      start_time.toISOString() +
      '&end_time=' +
      end_time.toISOString()
    );

    console.log("USER EVENTS:", event_data);

    if (event_data.success == 1 && event_data.data && Array.isArray(event_data.data)) {

      event_data.data.forEach(element => {

        if (!this.ShowsEventType(element.type)) return;

        let colour = '';
        let type = '';
        let guid = '';

        if (element.type == CalendarEventTypes.appointment) {
          type = 'appointment';
          colour = 'blue';
        }
        else if (element.type == CalendarEventTypes.assigned_work) {
          type = 'task';
          colour = 'red';
          guid = element.task_guid;
        }
        else if (element.type == CalendarEventTypes.blocked) {
          type = 'blocked';
          colour = 'black';
        }
        this.calendarAPI.addEvent({
          id: element.guid,
          title: element.title,
          start: element.start_time+"Z",
          end: element.end_time+"Z",
          color: colour,
          allDay: false,
          extendedProps: { type: type, guid: guid, email_guid: element.email_guid || '' }
        });

      });

    }

    this.loading_events = false;
    setTimeout(() => {
      if (this.calendarAPI) {
        this.calendarAPI.updateSize();
      }
    }, 0);

  }

  async GetCombinedEvents(start_time, end_time, team_members_to_combine: any[]) {

    if (!this.calendarAPI) return;

    this.loading_events = true;
    this.calendarAPI.removeAllEvents();

    const results = await Promise.all(
      team_members_to_combine.map(async tm => {
        try {
          const event_data: any = await this.apiService.get(
            'calendar/events?team_member=' +
            tm.guid +
            '&start_time=' +
            start_time.toISOString() +
            '&end_time=' +
            end_time.toISOString()
          );
          return { tm, events: (event_data?.success == 1 && Array.isArray(event_data.data)) ? event_data.data : [] };
        } catch {
          return { tm, events: [] };
        }
      })
    );

    // Merge by event guid so an appointment shared by multiple attendees only shows once,
    // with every attendee it was returned for listed in the title.
    const merged = new Map<string, { element: any; attendees: string[] }>();
    results.forEach(({ tm, events }) => {
      events.forEach(element => {
        const existing = merged.get(element.guid);
        if (existing) {
          existing.attendees.push(tm.display_name);
        } else {
          merged.set(element.guid, { element, attendees: [tm.display_name] });
        }
      });
    });

    merged.forEach(({ element, attendees }) => {

      if (!this.ShowsEventType(element.type)) return;

      let colour = '';
      let type = '';
      let guid = '';

      if (element.type == CalendarEventTypes.appointment) {
        type = 'appointment';
        colour = 'blue';
      }
      else if (element.type == CalendarEventTypes.assigned_work) {
        type = 'task';
        colour = 'red';
        guid = element.task_guid;
      }
      else if (element.type == CalendarEventTypes.blocked) {
        type = 'blocked';
        colour = 'black';
      }

      this.calendarAPI.addEvent({
        id: element.guid,
        title: `(${attendees.join(', ')}) ${element.title}`,
        start: element.start_time+"Z",
        end: element.end_time+"Z",
        color: colour,
        allDay: false,
        extendedProps: { type: type, guid: guid, email_guid: element.email_guid || '' }
      });

    });

    this.loading_events = false;
    setTimeout(() => {
      if (this.calendarAPI) {
        this.calendarAPI.updateSize();
      }
    }, 0);

  }

  handleDateClick(arg: any) {
    if (this.time_tracking_mode) {
      this.presentTimeTrackingModal(arg.date);
    } else {
      this.presentCreateCalendarEvent(arg.date);
    }
  }

  handleEventClick(arg: any) {
    const { type, guid, email_guid } = arg.event.extendedProps;
    if (email_guid) {
      this.presentEmailDetails(email_guid);
    } else if (type === 'appointment' || type === 'blocked') {
      this.presentRescheduleAppointment(arg.event.id, arg.event, type);
    } else if (type === 'task' && guid) {
      this.presentTaskDetails(guid);
    } else if (type === 'time_tracking' && guid) {
      this.presentTimeTrackingDetails(guid);
    }
  }

  async presentRescheduleAppointment(event_guid: string, calendar_event: any, event_type: string) {
    const data: any = await this.apiService.get(`calendar/events/${event_guid}`);
    if (!data?.success) return;

    const event = Array.isArray(data.data) ? data.data[0] : data.data;
    const force_type = event_type === 'blocked' ? CalendarEventTypes.blocked : CalendarEventTypes.appointment;
    const team_member_guids: string[] = (event.users || [])
      .filter((u: any) => u.type == 1)
      .map((u: any) => u.guid);

    const in_assigned_clients = this.globalService.ResolveAssignedClients(event);

    const start = new Date(calendar_event.start);
    const end = new Date(calendar_event.end);
    const length_minutes = Math.round((end.getTime() - start.getTime()) / 60000);

    const modal = await this.modalController.create({
      component: CreateComponent,
      cssClass: 'tall-modal secondary-modal',
      componentProps: {
        start_time: start,
        in_end_time: end,
        team_member: this.team_member,
        force_type: force_type,
        event_guid: event_guid,
        is_rescheduling: true,
        in_selected_users: team_member_guids,
        in_length: length_minutes,
        title: event.title,
        description: event.description,
        in_assigned_clients: in_assigned_clients
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data?.refresh) {
        this.RefreshCurrentView();
      }
    });

    return await modal.present();
  }

  async presentEmailDetails(email_guid: string) {
    const modal = await this.modalController.create({
      component: EmailDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: email_guid }
    });
    return await modal.present();
  }

  async presentTaskDetails(task_guid: string) {
    const modal = await this.modalController.create({
      component: TaskDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: task_guid },
      backdropDismiss: false
    });
    modal.onDidDismiss().then(() => {
      this.RefreshCurrentView();
    });
    return await modal.present();
  }

  async presentTimeTrackingModal(initial_start_time?: any) {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: initial_start_time ? { initial_start_time } : {}
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.success) this.RefreshCurrentView();
    });
    return await modal.present();
  }

  async presentTimeTrackingDetails(guid: string) {
    const entry = this.time_entries.find(e => e.guid === guid);
    if (!entry) {
      this.globalService.presentToastMessage('Time entry not found.', false);
      return;
    }
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { entry },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.deleted) this.RefreshCurrentView();
    });
    return await modal.present();
  }

  ToggleTimeTrackingMode(enabled: boolean) {
    this.SetEventFilter(enabled ? 'time_tracking' : 'appointments');
  }

  // append: add the time entries to the events already on the calendar (Show All) instead of replacing them
  private async GetTimeTrackingEvents(start_time: Date, end_time: Date, append = false) {
    if (!this.calendarAPI) return;
    if (!append) {
      this.loading_events = true;
      this.calendarAPI.removeAllEvents();
    }
    const filterGuid = !this.multi_view_mode && this.selected_team_member_guid !== this.ALL_TEAM_MEMBERS_VALUE ? this.selected_team_member_guid : null;
    // Start a day early so entries that begin before the visible range but run into it still show
    const range_start = new Date(start_time.getTime() - 24 * 60 * 60 * 1000);
    this.time_entries = await this.timeAndBillingService.GetEntries({
      user_guid: filterGuid,
      start: range_start.toISOString(),
      end: end_time.toISOString()
    });
    // The calendar may have been redrawn (or time tracking switched off) while the request was in flight
    if (!this.calendarAPI || (append ? this.event_filter !== 'all' : !this.time_tracking_mode)) return;
    if (!append) this.calendarAPI.removeAllEvents();
    // The view may have moved on while this was loading - another refresh adds its own
    if (append && (this.calendarAPI.view.currentStart.getTime() !== start_time.getTime() || this.calendarAPI.view.currentEnd.getTime() !== end_time.getTime())) return;
    // Multi View shows the team members that are ticked
    if (this.multi_view_mode) this.time_entries = this.time_entries.filter(entry => this.selected_team_member_guids.includes(entry.user_guid));
    this.time_entries.forEach(entry => {
      if (!entry.start_date || !entry.end_date) return;
      const start = new Date(entry.start_date);
      const end = new Date(entry.end_date);
      if (end < start_time || start > end_time) return;
      this.calendarAPI.addEvent({
        id: 'time_' + entry.guid,
        title: entry.title + (entry.user_name ? ' (' + entry.user_name + ')' : ''),
        start: entry.start_date,
        end: entry.end_date,
        color: '#198754',
        allDay: false,
        extendedProps: { type: 'time_tracking', guid: entry.guid }
      });
    });
    if (!append) this.loading_events = false;
    setTimeout(() => { if (this.calendarAPI) this.calendarAPI.updateSize(); }, 0);
  }

  async presentCreateCalendarEvent(start_time, force_type = null) {

    console.log("Forcing Type:", force_type);

    if (start_time == null) start_time = dayjs();

    const modal = await this.modalController.create({
      component: CreateComponent,
      cssClass: 'tall-modal secondary-modal',
      componentProps: {
        start_time: start_time,
        team_member: this.team_member,
        force_type: force_type
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data?.refresh) {
        this.RefreshCurrentView();
      }
    });

    return await modal.present();
  }

  async presentCreateAppointment(force_type = null) {
    this.presentCreateCalendarEvent(null, force_type);
  }

}